import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Utils } from '../../../../utils/utils';
import { ReleaseNotesDialogComponent } from '../releasenotesdialog/releasenotesdialog.component';

@Component({
    selector: 'app-peripheralsoftwaredetails-dialog',
    templateUrl: './peripheralsoftwaredetails-dialog.component.html',
    styleUrls: ['./peripheralsoftwaredetails-dialog.component.less'],
    standalone: false
})
export class PeripheralSoftwareDetailsDialogComponent implements OnInit {

    filteredFamilyCodes: FamilyCode[] = [];
    selectedFamilyCodeId!: number;
    editForm!: FormGroup;
    isCallSucceed: number = 0;
    originalFormValue!: any;
    isContractSpecific: boolean = false;

    constructor(@Inject(MAT_DIALOG_DATA) public data: {
        softwareReleaseForm: FormGroup,
        availableFamilyCodes: FamilyCode[],
        fileUploadId: string,
        availableLanguages: Language[],
        isContractSpecific?: boolean
    },
        private fb: FormBuilder
        , private dialogRef: MatDialogRef<PeripheralSoftwareDetailsDialogComponent>
        , private http: HttpClient
        , @Inject('BASE_URL') private baseUrl: string
        , private dialogForm: MatDialog
        , private toastrService: ToastrService
    ) {
        this.isContractSpecific = !!data.isContractSpecific;
    }

    ngOnInit(): void {
        // Clone the form value into a new FormGroup
        const originalValue = this.data.softwareReleaseForm.value;
        this.editForm = this.fb.group({
            ...originalValue,
            compatibilities: this.fb.array(originalValue.compatibilities ?? []),
            availableReleaseNotes: this.fb.array(
                (originalValue.availableReleaseNotes ?? []).map(note =>
                    this.fb.group({
                        softwareReleaseId: [note.softwareReleaseId],
                        softwareReleaseNotesId: [note.softwareReleaseNotesId],
                        language: [note.language],
                        fileName: [note.fileName],
                        isEnabled: [note.isEnabled],
                        createdByUser: [note.createdByUser],
                        createdAtUtc: [note.createdAtUtc],
                        changedAtUtc: [note.changedAtUtc],
                        changedByUser: [note.changedByUser],
                        file: [null]
                    })
                )
            ),
            fileUploadId: this.data.fileUploadId,
        });

        this.originalFormValue = JSON.parse(JSON.stringify(this.editForm.value));
        this.selectedFamilyCodeId = this.editForm.value.familyCodeId;
        this.filteredFamilyCodes = this.data.availableFamilyCodes;
    }

    getFamilyCodeText(familyCodeId: number) {
        let famcode = this.data.availableFamilyCodes.find(x => x.familyCodeId === familyCodeId);
        if (famcode)
            return `${famcode.familyCode} - ${famcode.familyCodeName}`;
        else
            return "";
    }

    onFamilyCodesFilter(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.filterFamilyCodes(value.toLowerCase());
    }

    filterFamilyCodes(value: string) {

        // If value is empty, user cleared the input
        if (!value) {
            this.selectedFamilyCodeId = -1;
            return;
        }

        this.filteredFamilyCodes = this.data.availableFamilyCodes.filter(fc =>
            this.getFamilyCodeText(fc.familyCodeId).toLowerCase().includes(value.toLowerCase())
        );

        // Check if value matches any family code
        const matchedFamilyCode = this.filteredFamilyCodes.find(
            fc => this.getFamilyCodeText(fc.familyCodeId).toLowerCase() === value.toLowerCase()
        );
        if (!matchedFamilyCode) {
            this.selectedFamilyCodeId = -1;
            return;
        } else {
            this.selectedFamilyCodeId = matchedFamilyCode.familyCodeId;
        }
    }

    onFamilyCodeSelected(event: MatAutocompleteSelectedEvent) {
        const selectedId = event.option.value;
        this.editForm.patchValue({ familyCodeId: selectedId });
        this.selectedFamilyCodeId = selectedId;
    }

    onFamilyCodeFormFieldClick() {
        this.selectedFamilyCodeId = -1;
        this.filteredFamilyCodes = this.data.availableFamilyCodes; // Reset filter
    }

    onFileChange(event: any) {
        const files = (event?.target as HTMLInputElement)?.files;
        if (files && files.length) {
            const file = files[0];
            this.editForm.get('softwareFile')?.setValue(file);
            this.editForm.get('softwareFileName')?.setValue(file.name);
        } else {
            this.editForm.get('softwareFile')?.setValue(null);
        }
    }

    anyChange(): boolean {
        return this.pendingChanges();
    }

    pendingChanges(): boolean {
        return this.diffValues()
            && this.selectedFamilyCodeId !== -1
            && this.editForm.value.peripheralSoftwareVersion !== "" && this.editForm.value.peripheralSoftwareVersion.length <= 50
            && (this.editForm.value.isNewRow ? this.editForm.value.softwareFile != null : true);
    }

    diffValues(): boolean {
        const current = this.editForm.value;
        const original = this.originalFormValue;

        return this.selectedFamilyCodeId !== original.familyCodeId
            || current.peripheralSoftwareVersion !== original.peripheralSoftwareVersion
            || current.softwareFileName !== original.softwareFileName;
    }

    onCancel(): void {
        this.dialogRef.close({
            canceled: true,
            updatedReleaseNotes: this.editForm.get('availableReleaseNotes')?.value
        });
    }

    onSave() {
        if (this.anyChange()) {

            let formData: FormData = new FormData();
            formData.append(this.editForm.value.fileUploadId, this.editForm.value.softwareFile);
            let modifiedPeripheralSoftware: EMTPeripheralSoftwareItem[] = [];
            let newPeripheralSoftware: EMTPeripheralSoftwareItem[] = [];
            this.editForm.get('familyCodeId')?.setValue(this.selectedFamilyCodeId);
            if (this.editForm.value.isNewRow) {
                const newVersion = { ...this.editForm.value, isContractSpecific: this.isContractSpecific };
                newPeripheralSoftware.push(newVersion);
            } else {
                modifiedPeripheralSoftware.push({
                    ...this.editForm.value,
                    compatibilities: (this.editForm.value.compatibilities || []).map((c: any) => c.globalControllerGroupId)
                });
            }
            let versionChanges = { 'modifiedVersions': modifiedPeripheralSoftware, 'newVersions': newPeripheralSoftware }
            formData.append('versionChanges', JSON.stringify(versionChanges));

            Utils.httpUpdatePeripheralSoftwareVersions(
                this.http
                , this.baseUrl
                , formData
                , this
                , function (tthis: PeripheralSoftwareDetailsDialogComponent, result: any) {
                    tthis.dialogRef.close({
                        success: tthis.isCallSucceed === 1,
                        errorMssg: result?.error && result.errorMssg ? result.errorMssg : null
                    });
                }
            )
        }
    }

    openReleaseNotes(formGroup: FormGroup | null, isNew: boolean = false) {
        const dialogRef = this.dialogForm.open(ReleaseNotesDialogComponent, {
            height: 'auto',
            width: '600px',
            data: {
                releaseNotesForm: formGroup ?? this.fb.group({
                    fileName: new FormControl(''),
                    file: new FormControl(null),
                }),
                availableLanguages: isNew ? this.data.availableLanguages.filter(lang =>
                    !this.editForm.value.availableReleaseNotes?.some(
                        note => note.language === lang.LanguageCultureName
                    )
                ) : this.data.availableLanguages,
                isNew,
                softwareReleaseId: this.editForm.value.peripheralSoftwareVersionID,
                softwareType: 'peripheral',
                metadataLabel: this.editForm.value.peripheralSoftwareVersion
            },
            panelClass: 'details-dialog-panel',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success && result.updatedReleaseNotes) {
                const releaseNotesArray = new FormArray(
                    result.updatedReleaseNotes.map(note =>
                        this.fb.group({
                            softwareReleaseNotesId: [note.softwareReleaseNotesId],
                            softwareReleaseId: [note.softwareReleaseId],
                            language: [note.language],
                            fileName: [note.fileName],
                            isEnabled: [note.isEnabled],
                            createdByUser: [note.createdByUser],
                            createdAtUtc: [note.createdAtUtc],
                            changedAtUtc: [note.changedAtUtc],
                            changedByUser: [note.changedByUser],
                            file: [null]
                        })
                    )
                );

                // Replace the old FormArray with the new one so the UI refreshes
                this.editForm.setControl('availableReleaseNotes', releaseNotesArray);
                this.editForm.updateValueAndValidity();

                this.toastrService.success('Release notes successfully saved!')
            }
            else if (!result?.canceled) {
                this.toastrService.error('Save Release notes could not be performed. Please, contact with administrator.')
            }
            else {
                const releaseNotesArray = this.editForm.get('availableReleaseNotes') as FormArray;
                if (releaseNotesArray && formGroup) {
                    // Get the softwareReleaseNotesId safely
                    let targetId: any;
                    if (formGroup instanceof FormGroup) {
                        targetId = formGroup.get('softwareReleaseNotesId')?.value;
                    } else {
                        targetId = formGroup['softwareReleaseNotesId'];
                    }
                    // Find the index of the release note to update
                    const idx = releaseNotesArray.controls.findIndex(ctrl =>
                        ctrl.get('softwareReleaseNotesId')?.value === targetId
                    );
                    if (idx !== -1) {
                        releaseNotesArray.at(idx).get('changedByUser')?.setValue(result.changedByUser);
                        this.editForm.updateValueAndValidity();
                    }
                }
            }

        });

    }

    getReleaseNoteLabelText(language: string) {
        const lang = this.data.availableLanguages.find(l => l.LanguageCultureName === language);
        if (lang) {
            // Get first part of lang.Name before '-'
            const namePart = lang.Name.split('-')[0].trim();
            // Get second part of lang.LanguageCultureName after '-'
            const cultureParts = lang.LanguageCultureName.split('-');
            const culturePart = cultureParts.length > 1 ? cultureParts[1].trim() : '';
            return `${namePart} - ${culturePart}`;
        } else {
            return "";
        }
    }
}
